import { useTranslation } from 'react-i18next'
import Header from '@/components/layout/header'
import Footer from '@/components/layout/footer'
import Container from '@/components/ui/container'
import Seo from '@/components/seo/seo'
import { breadcrumbSchema } from '@/components/seo/json-ld'
import { resolveLang } from '@/lib/site'

interface LegalSection {
  id: string
  title: string
  paragraphs: string[]
}

interface LegalPageProps {
  documentKey: 'privacy' | 'terms'
  path: '/privacy' | '/terms'
}

export default function LegalPage({ documentKey, path }: LegalPageProps) {
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.resolvedLanguage)

  const title = t(`legal.${documentKey}.title`) as string
  const lastUpdated = t(`legal.${documentKey}.lastUpdated`) as string
  const intro = t(`legal.${documentKey}.intro`, { returnObjects: true }) as string[]
  const sections = t(`legal.${documentKey}.sections`, { returnObjects: true }) as LegalSection[]
  const seoTitle = t(`seo.${documentKey}.title`) as string
  const seoDescription = t(`seo.${documentKey}.description`) as string
  const lastUpdatedLabel = t('legal.common.lastUpdatedLabel') as string
  const tocLabel = t('legal.common.tocLabel') as string
  const backToTopLabel = t('legal.common.backToTop') as string

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={path}
        jsonLd={breadcrumbSchema(lang, [
          { name: 'Bravic', path: '/' },
          { name: seoTitle, path },
        ])}
      />
      <Header />
      <main className="relative pt-24 md:pt-32 lg:pt-40 pb-16 md:pb-24 overflow-hidden bg-[radial-gradient(circle_at_20%_30%,rgba(0,166,147,0.05)_0%,transparent_50%),radial-gradient(circle_at_80%_70%,rgba(91,218,198,0.03)_0%,transparent_50%)]">
        <Container>
          <header className="max-w-3xl mb-12 md:mb-16">
            <span className="text-primary font-body text-xs uppercase tracking-[0.3em] mb-6 block">
              {lastUpdatedLabel}: {lastUpdated}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95] text-on-surface mb-8">
              {title}
            </h1>
            <div className="space-y-4 text-on-surface-variant text-base leading-relaxed [&_strong]:text-on-surface [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline">
              {intro.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-16">
            <aside className="lg:sticky lg:top-28 self-start">
              <h2 className="text-primary font-bold uppercase tracking-widest text-[0.65rem] mb-4">
                {tocLabel}
              </h2>
              <nav className="flex flex-col gap-2 text-sm text-on-surface-variant">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="hover:text-primary transition-colors"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </aside>

            <article className="max-w-3xl space-y-12 md:space-y-14">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-on-surface mb-5">
                    {section.title}
                  </h2>
                  <div className="space-y-4 text-on-surface-variant text-[0.95rem] md:text-base leading-relaxed [&_strong]:text-on-surface [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline [&_em]:italic [&_code]:font-mono [&_code]:text-[0.85em] [&_code]:bg-surface-container [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_li]:leading-relaxed">
                    {section.paragraphs.map((p, i) => (
                      <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                  </div>
                </section>
              ))}

              <div className="pt-4">
                <a
                  href="#top"
                  onClick={(e) => {
                    e.preventDefault()
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                  className="text-primary text-sm uppercase tracking-widest font-semibold hover:underline"
                >
                  ↑ {backToTopLabel}
                </a>
              </div>
            </article>
          </div>
        </Container>
      </main>
      <Footer />
    </>
  )
}
